/*
  * Copyright Mission 42 Ventures, Inc. All rights reserved.
  * Unauthorized copying, distribution, or use is prohibited.
  */

import React, { useState } from 'react';
import { storageApiRef, useApi } from '@backstage/core-plugin-api';
import useObservable from 'react-use/lib/useObservable';
import { Box, Button, IconButton, Typography, makeStyles } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import LockIcon from '@material-ui/icons/LockOutlined';

import { REQUEST_TEAM_LABEL, REQUEST_TEAM_URL } from '../access/requestAccess';

const STORAGE_BUCKET = 'home';
const DISMISS_KEY = 'read-only-notice-dismissed';

const useStyles = makeStyles(theme => ({
  banner: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5, 2),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    // Subtle accent tint -- informational, not an error.
    backgroundColor:
      theme.palette.type === 'dark'
        ? 'rgba(255, 110, 91, 0.10)'
        : 'rgba(255, 110, 91, 0.07)',
  },
  icon: {
    color: theme.palette.primary.main,
    marginTop: 2,
    flexShrink: 0,
  },
  body: {
    flex: 1,
    minWidth: 0,
  },
  title: {
    fontWeight: 600,
    fontSize: '0.9375rem',
  },
  text: {
    color: theme.palette.text.secondary,
    fontSize: '0.8125rem',
    marginTop: theme.spacing(0.25),
  },
  actions: {
    marginTop: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  dismiss: {
    padding: theme.spacing(0.5),
    marginTop: -theme.spacing(0.5),
    marginRight: -theme.spacing(0.5),
    flexShrink: 0,
  },
}));

/**
 * Read-only access notice (P1 / PLAT-254). Shown on Home to a signed-in user
 * with no team membership (Tier-1): they can browse the catalog but every
 * mutation (create service, run a template) is denied by the permission
 * policy. Rather than letting them hit a generic 403 with no recovery path,
 * this explains the state and links to the GitHub team where they can request
 * to join (which grants Tier-2 / write).
 *
 * Caller (HomePage) owns the gate -- it only renders this once ownership has
 * resolved with zero team refs and the user isn't using the "View as" lens.
 * Dismissible; the choice persists in the same storage bucket as the scope.
 */
export const ReadOnlyAccessNotice = () => {
  const classes = useStyles();
  const storage = useApi(storageApiRef).forBucket(STORAGE_BUCKET);
  const snap = useObservable(
    storage.observe$<boolean>(DISMISS_KEY),
    storage.snapshot<boolean>(DISMISS_KEY),
  );
  // Optimistic local hide so dismiss feels instant even before the observable
  // round-trips.
  const [hidden, setHidden] = useState(false);

  if (hidden || snap?.value === true) return null;

  const dismiss = () => {
    setHidden(true);
    storage.set(DISMISS_KEY, true);
  };

  return (
    <Box className={classes.banner}>
      <LockIcon className={classes.icon} fontSize="small" />
      <Box className={classes.body}>
        <Typography component="div" className={classes.title}>
          You have read-only access
        </Typography>
        <Typography component="div" className={classes.text}>
          You can browse the catalog, docs, and pull requests, but creating
          services, running templates, and other changes require membership in
          a team. Request to join {REQUEST_TEAM_LABEL} to get write access.
        </Typography>
        <Box className={classes.actions}>
          <Button
            variant="outlined"
            size="small"
            color="primary"
            href={REQUEST_TEAM_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            Request to join {REQUEST_TEAM_LABEL}
          </Button>
        </Box>
      </Box>
      <IconButton
        className={classes.dismiss}
        size="small"
        aria-label="Dismiss read-only notice"
        onClick={dismiss}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};
